import {
	useDeleteProjectMutation,
	useGetAllRootProjectSummariesLazyQuery,
	useGetAllSubProjectsLazyQuery,
	useSearchProjectsLazyQuery,
	OrderType,
	ValidationStatus,
	type DocumentStatus,
	ProjectStatus,
} from "@repo/server-api";
import { type IdWithName } from "@repo/shared/common";
import { useNotification } from "@repo/shared/components";
import { t } from "i18next";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { ProjectSummary } from "../types/ProjectTypes";
import { emptyBudgetStatus } from "../types/ProjectTypes";
import usePersistentState from "@repo/legacy/store/usePersistentState";

export type PaginationModel = {
	page: number;
	pageSize: number;
};

export type UseProjectListReturn = {
	// Data
	rows: ProjectSummary[];
	subRows: ProjectSummary[];
	rowCount: number;
	isLoading: boolean;
	matchedIds: string[];

	// Filters
	searchValue: string;
	setSearchValue: (value: string) => void;
	debouncedSearchValue: string;
	showInactive: boolean;
	setShowInactive: (value: boolean) => void;
	selectedClient: IdWithName | null;
	setSelectedClient: (client: IdWithName | null) => void;
	selectedStatuses: ProjectStatus[];
	setSelectedStatuses: (statuses: ProjectStatus[]) => void;

	// Pagination
	paginationModel: PaginationModel;
	setPaginationModel: (model: PaginationModel) => void;

	// Actions
	expandRow: (id: string) => Promise<void>;
	deleteProject: (id: string) => Promise<void>;
	refresh: () => void;
};

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(timer);
		};
	}, [value, delay]);

	return debouncedValue;
}

// Map GraphQL node to ProjectSummary
const mapProjectNodeToSummary = (node: {
	id: string;
	name: string;
	number: string;
	status: DocumentStatus | ProjectStatus;
	isManualStatusOverride: boolean;
	validationStatus: ValidationStatus;
	path: string[];
	projectType: ProjectSummary["projectType"];
	isGroup: boolean;
	range: { from: string; to: string };
	isDeleted: boolean;
	parentProjectId?: string | null;
	parentProjectName?: string | null;
	budget: {
		hasTimeLimit: boolean;
		hours: number;
		hasAmountLimit: boolean;
		amount: number;
	};
	budgetStatus: {
		totalBudgetAmount: number;
		totalBudgetMinutes: number;
		spentAmount: number;
		spentMinutes: number;
		remainingAmount: number;
		remainingMinutes: number;
		utilizationPercent: number;
		openMinutes: number;
		pendingMinutes: number;
		approvedMinutes: number;
	};
	client?: { id: string; displayName: string } | null;
	contractor?: { id: string; displayName: string } | null;
}): ProjectSummary => {
	return {
		id: node.id,
		name: node.name,
		number: node.number,
		status: node.status,
		isManualStatusOverride: node.isManualStatusOverride,
		validationStatus: node.validationStatus,
		projectType: node.projectType,
		type: node.isGroup ? "group" : "leaf",
		isGroup: node.isGroup,
		path: node.path,
		joinedPath: node.path.join("/"),

		startDate: node.range.from,
		endDate: node.range.to,
		isDeleted: node.isDeleted,
		parentProject: !node.parentProjectId
			? null
			: {
					id: node.parentProjectId,
					name: node.parentProjectName ?? "Not available",
				},

		budgetHours: node.budget.hours ?? 0,
		hasBudgetHours: node.budget.hasTimeLimit,
		budgetPrice: node.budget.amount ?? 0,
		hasBudgetPrice: node.budget.hasAmountLimit,

		budgetStatus: node.budgetStatus ?? emptyBudgetStatus,

		contactInformation: {
			id: null,
			client: node.client ? { id: node.client.id, name: node.client.displayName } : null,
			contractor: node.contractor
				? { id: node.contractor.id, name: node.contractor.displayName }
				: null,
			endCustomer: null,
		},
	};
};

export function useProjectList(): UseProjectListReturn {
	const [rows, setRows] = useState<ProjectSummary[]>([]);
	const [subRows, setSubRows] = useState<ProjectSummary[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [matchedIds, setMatchedIds] = useState<string[]>([]);

	const [searchValue, setSearchValue] = useState("");
	const [showInactive, setShowInactive] = usePersistentState("show-inactive", false);
	const [selectedClient, setSelectedClient] = useState<IdWithName | null>(null);
	const [selectedStatuses, setSelectedStatuses] = useState<ProjectStatus[]>([]);
	const [paginationModel, setPaginationModel] = useState<PaginationModel>({
		page: 0,
		pageSize: 20,
	});

	const { showNotification } = useNotification();
	const isDeletingRef = useRef(false);

	// Debounce search value
	const debouncedSearchValue = useDebounce(searchValue, 300);

	const [deleteProjectMutation] = useDeleteProjectMutation();

	// Query for root projects (used when no search)
	const [getProjects] = useGetAllRootProjectSummariesLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	// Query for search with ancestors
	const [searchProjects] = useSearchProjectsLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	// Query for sub-projects (for tree expansion)
	const [getSubProjects] = useGetAllSubProjectsLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	// Fetch data with current filters
	const fetchData = useCallback(async () => {
		if (isDeletingRef.current) return;

		setIsLoading(true);
		setSubRows([]);

		// Use search query when there's a search term, otherwise use root projects query
		if (debouncedSearchValue) {
			const response = await searchProjects({
				variables: {
					first: paginationModel.pageSize,
					after: (paginationModel.pageSize * paginationModel.page).toString(),
					type: null,
					name: debouncedSearchValue,
					order: OrderType.Asc,
					showDeleted: showInactive as boolean,
					clientId: selectedClient?.id ?? null,
				},
			});

			if (response.data?.searchProjects) {
				const mappedRows = response.data.searchProjects.projects.map(mapProjectNodeToSummary);
				setRows(mappedRows);
				setRowCount(response.data.searchProjects.totalCount);
				setMatchedIds(response.data.searchProjects.matchedIds);
			}
		} else {
			const response = await getProjects({
				variables: {
					first: paginationModel.pageSize,
					after: (paginationModel.pageSize * paginationModel.page).toString(),
					type: null,
					name: null,
					order: OrderType.Asc,
					showDeleted: showInactive as boolean,
					clientId: selectedClient?.id ?? null,
				},
			});

			if (response.data?.allRootProjects?.nodes) {
				const mappedRows = response.data.allRootProjects.nodes.map(mapProjectNodeToSummary);
				setRows(mappedRows);
				setRowCount(response.data.allRootProjects.totalCount);
				setMatchedIds([]);
			}
		}

		setIsLoading(false);
	}, [
		getProjects,
		searchProjects,
		paginationModel,
		debouncedSearchValue,
		showInactive,
		selectedClient,
	]);

	// Expand a row (load sub-projects)
	const expandRow = useCallback(
		async (id: string) => {
			setIsLoading(true);

			const response = await getSubProjects({
				variables: {
					projectId: id,
					showDeleted: showInactive as boolean,
				},
			});

			if (response.data?.allSubProjects) {
				const newRows = response.data.allSubProjects.map(mapProjectNodeToSummary);

				setSubRows((prevSubRows) => {
					const existingPaths = prevSubRows.map((row) => row.joinedPath);
					const uniqueNewRows = newRows.filter((row) => !existingPaths.includes(row.joinedPath));
					return [...prevSubRows, ...uniqueNewRows];
				});
			}

			setIsLoading(false);
		},
		[getSubProjects, showInactive],
	);

	// Delete a project
	const deleteProject = useCallback(
		async (id: string) => {
			isDeletingRef.current = true;

			// Optimistic update
			setRows((currentRows) => {
				let tmpRows = currentRows.filter((row) => row.id !== id);
				let parent: ProjectSummary | undefined = undefined;
				let parentChildrenCount = 0;

				setSubRows((currentSubRows) => {
					const tmpSubRows = currentSubRows.filter((row) => {
						if (row.id === id) {
							parent = currentRows.find((r) => r.id === row.parentProject?.id);
							parentChildrenCount = currentSubRows.filter(
								(r) => r.parentProject?.id === parent?.id,
							).length;
							return false;
						}
						if (row.parentProject?.id === id) {
							return false;
						}
						return true;
					});
					return tmpSubRows;
				});

				if (parent && parentChildrenCount === 1) {
					tmpRows = tmpRows.map((row) => {
						if (row.id === parent?.id) {
							return { ...row, isGroup: false };
						}
						return row;
					});
				}

				return tmpRows;
			});

			setRowCount((prev) => Math.max(0, prev - 1));

			try {
				const deleteResult = await deleteProjectMutation({
					variables: { id },
					update: (cache) => {
						cache.evict({ id: `Project:${id}` });
						cache.gc();
					},
				});

				if (!deleteResult.data?.deleteProject) {
					isDeletingRef.current = false;
					fetchData();
					showNotification(t("project-delete-failed"), "error");
					return;
				}

				showNotification(t("project-deleted-successfully"), "success");
			} catch {
				isDeletingRef.current = false;
				fetchData();
				showNotification(t("project-delete-failed"), "error");
			} finally {
				setTimeout(() => {
					isDeletingRef.current = false;
				}, 100);
			}
		},
		[deleteProjectMutation, fetchData, showNotification],
	);

	// Refresh data
	const refresh = useCallback(() => {
		fetchData();
	}, [fetchData]);

	// Fetch data when dependencies change
	useEffect(() => {
		fetchData();
	}, [paginationModel, debouncedSearchValue, showInactive, selectedClient]);

	// Client-side status filter applied on top of backend results
	const filteredRows = useMemo(() => {
		if (selectedStatuses.length === 0) return rows;

		// Collect ancestor IDs of subrows that match the filter
		const matchingSubRowIds = new Set(
			subRows
				.filter((row) => selectedStatuses.includes(row.status as ProjectStatus))
				.map((row) => row.id),
		);
		const ancestorIds = new Set<string>();
		const collectAncestors = (rowId: string) => {
			const row = subRows.find((r) => r.id === rowId);
			if (row?.parentProject?.id) {
				ancestorIds.add(row.parentProject.id);
				collectAncestors(row.parentProject.id);
			}
		};
		matchingSubRowIds.forEach(collectAncestors);

		return rows.filter(
			(row) => selectedStatuses.includes(row.status as ProjectStatus) || ancestorIds.has(row.id),
		);
	}, [rows, subRows, selectedStatuses]);

	const filteredSubRows = useMemo(() => {
		if (selectedStatuses.length === 0) return subRows;

		// Collect matching subrow IDs and their ancestors
		const matchingIds = new Set(
			subRows
				.filter((row) => selectedStatuses.includes(row.status as ProjectStatus))
				.map((row) => row.id),
		);
		const ancestorIds = new Set<string>();
		const collectAncestors = (rowId: string) => {
			const row = subRows.find((r) => r.id === rowId);
			if (row?.parentProject?.id) {
				ancestorIds.add(row.parentProject.id);
				collectAncestors(row.parentProject.id);
			}
		};
		matchingIds.forEach(collectAncestors);

		return subRows.filter((row) => matchingIds.has(row.id) || ancestorIds.has(row.id));
	}, [subRows, selectedStatuses]);

	return {
		rows: filteredRows,
		subRows: filteredSubRows,
		rowCount,
		isLoading,
		matchedIds,

		searchValue,
		setSearchValue,
		debouncedSearchValue,
		showInactive,
		setShowInactive,
		selectedClient,
		setSelectedClient,
		selectedStatuses,
		setSelectedStatuses,

		paginationModel,
		setPaginationModel,

		expandRow,
		deleteProject,
		refresh,
	};
}
